import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import ListFilter from "../../listFilter";
import Button from "../../../../../components/buttons/buttons";
import { Beat } from "../../../../../plugins/spinners.plugin";
import TableSkeleton from "../../../../../components/skeleton/table";
import EmptyState from "../../../../../assets/images/emptyState/orders.svg";
import Delivered from "../../../../../assets/images/icons/delivered.svg";
import Unshipped from "../../../../../assets/images/icons/unshipped.svg";
import Shipped from "../../../../../assets/images/icons/shipped.svg";
import Closed from "../../../../../assets/images/icons/closed.svg";
import { PaginationNoRoute } from "../../../../../components/pagination/paginationNoRoute";
import {
  filterMyRestaurantOrderAction,
  searchMyRestaurantOrderAction
} from "../../../../../redux/restaurantOrder/restaurantOrderActions";
import ViewOrderModal from "../../../../../components/modals/viewOrder/restaurantOrder";
import { AllFiltersModal } from "../../../../../components/modals/allFiltersModal";
import FilterUserRestaurantOrdersDropDown from "../../../../../components/dropDown/filter/filterUserRestaurantOrders";
import restaurantType from "../../../../../redux/restaurantOrder/restaurantOrderTypes";
import { writeFileWithXLSX } from "../../../../../components/exports/xlsx";

function numberWithCommas(x) {
  return x?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function createTableData(orders) {
  return orders?.map((order) => ({
    id: order?.id,
    status: order?.status,
    items: order?.order_items?.length,
    amount: order?.order_amount,
    created_at: order?.created_at,
  }));
}

function createTableDataDownload(orders) {
  return orders?.map((order) => ({
    "Order ID": order?.id,
    "Status": order?.status,
    "Items": order?.order_items?.length,
    "Amount": order?.order_amount,
    "Created Date": moment(order?.created_at).format("MMM DD, YYYY HH:mm"),
    "Customer": order?.recipient_name,
    "Phone": order?.recipient_phone,
    "Address": order?.recipient_address,
  }));
}

export default function MyRestaurantOrders() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [isFilterOpen, setFilterState] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [input, setInput] = useState({ search: "" });
  const [data, setData] = useState(null);
  const [payLoad, setPayLoad] = useState({ end_date: new Date() });

  const viewOrderModalRef = useRef(null);
  const { myFilteredOrders, search, isLoading, downloading, searching } = useSelector((state) => state.restaurantOrder);

  // Use search results if searching, otherwise use filtered orders
  const allOrders = isSearchOpen ? { data: search } : myFilteredOrders;

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page]);

  useEffect(() => {
    dispatch(filterMyRestaurantOrderAction(payLoad, page));
    // eslint-disable-next-line
  }, [page]);


  const goToTrack = (id) => {
    navigate(`/track/${id}`);
  };

  const groupedData = allOrders?.data?.reduce((acc, obj) => {
    const d = new Date(obj.created_at);
    let month = months[d.getMonth()];
    let year = d.getFullYear();
    const key = `${month} ${year}`;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(obj);
    return acc;
  }, {});

  const handleChange = (e) => {
    const { name, value } = e.target;
    e.preventDefault();
    setInput({ ...input, [name]: value });
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      doSearch();
    }
  };

  const showSearch = (search) => {
    let dataSet = createTableData(search);
    setData(dataSet);
    setIsSearchOpen(true);
  };

  const removeSearch = () => {
    if (input?.search?.length === 0) {
      let dataSet = createTableData(allOrders?.data);
      setData(dataSet);
      setIsSearchOpen(false);
      dispatch({
        type: restaurantType["SEARCH_RESTAURANT_ORDERS"],
        payLoad: { data: [] },
      });
    }
  };

  async function doSearch() {
    try {
      let response = await dispatch(searchMyRestaurantOrderAction(input));
      if (response?.status === "success") {
        showSearch(response?.data);
      }
      if (!response?.status) {
        showSearch([]);
      }
    } catch (error) { }
  }

  useEffect(() => {
    if (input?.search?.length === 0) {
      removeSearch();
    }
    // eslint-disable-next-line
  }, [input, dispatch]);

  const handleDownload = async () => {
    const response = await dispatch(
      filterMyRestaurantOrderAction({
        ...payLoad,
        pagination: 0,
      })
    );
    if (response) {
      const newData = createTableDataDownload(response?.data);
      writeFileWithXLSX(newData, {
        filename: "My_Restaurant_Orders.xlsx",
        download: true,
      });
    }
  };

  return (
    <>
      <div className="text-left ">
        <div>
          <ListFilter
            results={allOrders?.to}
            totalResults={allOrders?.total}
            onChange={handleChange}
            handleKeyDown={handleKeyDown}
            searchPlaceholder={"Search for order by order id"}
            searchValue={input.search}
            filter={
              <AllFiltersModal
                isFilterOpen={isFilterOpen}
                setFilterState={setFilterState}
              >
                <FilterUserRestaurantOrdersDropDown
                  isFilterOpen={isFilterOpen}
                  setFilterState={setFilterState}
                  payLoad={payLoad}
                  setPayLoad={setPayLoad}
                  page={page}
                />
              </AllFiltersModal>
            }
          >
            <Button.Secondary
              disabled={!data && !allOrders?.data}
              title={downloading ? <Beat color={"#CE2600"} /> : "Download CSV"}
              onClick={handleDownload}
            />
          </ListFilter>
        </div>

        {(isLoading || searching) ? (
          <TableSkeleton />
        ) : allOrders?.data?.length > 0 ? (
          <>
            {Object.entries(groupedData).map(([monthYear, objs]) => (
              <div key={monthYear}>
                <h1 className=" text-xl font-medium py-6 uppercase ">
                  {monthYear}
                </h1>
                <div className="sm:border-b-2 dark:border-none"></div>
                <ul className="sm:p-2 my-1">
                  {objs.map((obj, index) => (
                    <li
                      key={index}
                      className="border-b border-gray-300 dark:border-none grid grid-cols-4 sm:grid-cols-5 gap-2 items-center py-2"
                    >
                      <div className="hidden sm:flex text-sm sm:text-base">
                        <p>{moment(obj?.created_at).format("MMM DD, HH:mm")}</p>
                      </div>

                      {obj?.status === "delivered" && (
                        <div className="flex gap-1 sm:gap-3 items-center">
                          <img
                            src={Delivered}
                            alt="icon"
                            className="w-3 sm:w-4 h-3 sm:h-4 mt-1"
                          />
                          <p className="text-[#00D220] sm:ml-1 text-sm sm:text-lg font-medium capitalize">
                            {obj?.status}
                          </p>
                        </div>
                      )}
                      {obj?.status === "shipped" && (
                        <div className="flex gap-1 sm:gap-3 items-center">
                          <img
                            src={Shipped}
                            alt="icon"
                            className="w-3 sm:w-4 h-3 sm:h-4 mt-1"
                          />
                          <p className="text-[#046ECF] sm:ml-1 text-sm sm:text-lg font-medium capitalize">
                            {obj?.status}
                          </p>
                        </div>
                      )}
                      {obj?.status === "unshipped" && (
                        <div className="flex gap-1 sm:gap-3 items-center">
                          <img
                            src={Unshipped}
                            alt="icon"
                            className="w-3 sm:w-4 h-3 sm:h-4 mt-1"
                          />
                          <p className="text-primary sm:ml-1 text-sm sm:text-lg font-medium capitalize">
                            {obj?.status}
                          </p>
                        </div>
                      )}
                      {obj?.status === "closed" && (
                        <div className="flex gap-1 sm:gap-3 items-center">
                          <img
                            src={Closed}
                            alt="icon"
                            className="w-3 sm:w-4 h-3 sm:h-4 mt-1"
                          />
                          <p className="text-[#FFA927] sm:ml-1 text-sm sm:text-lg font-medium capitalize">
                            {obj?.status}
                          </p>
                        </div>
                      )}
                      {obj?.status === "queued" && (
                        <div className="flex gap-1 sm:gap-3 items-center">
                          <img
                            src={Closed}
                            alt="icon"
                            className="w-3 sm:w-4 h-3 sm:h-4 mt-1"
                          />
                          <p className="text-[#FFA927] sm:ml-1 text-sm sm:text-lg font-medium capitalize">
                            {obj?.status}
                          </p>
                        </div>
                      )}
                      {obj?.status === "pending" && (
                        <div className="flex gap-1 sm:gap-3 items-center">
                          <img
                            src={Unshipped}
                            alt="icon"
                            className="w-3 sm:w-4 h-3 sm:h-4 mt-1"
                          />
                          <p className="text-[#FFA927] sm:ml-1 text-sm sm:text-lg font-medium capitalize">
                            {obj?.status}
                          </p>
                        </div>
                      )}
                      <p className="text-gray-400 text-sm sm:text-base ml-4 font-normal">
                        {obj?.order_items?.length} item
                        {obj?.order_items?.length > 1 && "s"}
                      </p>
                      <p className="text-sm sm:text-lg font-medium">
                        ₦{numberWithCommas(obj?.order_amount.toFixed(2))}
                      </p>
                      {obj?.status === "shipped" ? (
                        <Button.Secondary
                          title={"Track"}
                          type="button"
                          onClick={() => goToTrack(obj?.id)}
                        />
                      ) : (
                        <Button.Secondary
                          title={"View"}
                          type="button"
                          onClick={() => {
                            viewOrderModalRef.current.open();
                            viewOrderModalRef.current.getId(obj?.id);
                          }}
                        />
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            {!isSearchOpen && <PaginationNoRoute data={allOrders} page={page} setPage={setPage} />}
          </>
        ) : (
          <div>
            <div className="flex justify-center py-12">
              <img src={EmptyState} alt="Empty state" className="mt-10" />
            </div>
            <p className="my-10 text-center">You do not have any orders yet.</p>
          </div>
        )}
      </div>
      <ViewOrderModal ref={viewOrderModalRef} visibility={false} />
    </>
  );
}
